import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { compareObjects } from '@utils/compareObjects';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useDebounce } from '@hooks/useDebounce';
import { useEntityFormMaxHeight } from '@hooks/useEntityFormMaxHeight';
import { Button } from '@components/Core/Button';
import { ImageUploader } from '@components/Core/ImageUploader/ImageUploader';
import Popup from '@components/Core/Popup/Popup';
import { SECOND_POPUP_WIDTH } from '@components/Core/Popup/constants';
import { Input } from '@inputs/Input/Input';
import DatePickerRow from '@inputs/DatePickerComponents/DatePickerRow';
import UploadJSONInput from '@inputs/UploadJSONInput';
import { ConfirmationPopup } from '@popups/ConfirmationPopup';
import { StatusPopup } from '@popups/StatusPopup';
import { CataloguesTransFields } from '@pages/Catalogues/types';
import { cataloguesTranslations } from '@pages/Catalogues/translations';
import ChevronIcon from '@icons/inputs/chevron_icon.svg?component';
import { catalogueFormTranslations } from './translations';
import { approveRequiredFields } from './constants';
import { ICatalogueFormProps, ICatalogueForm, CatalogueFormTransFields } from './types';

const CatalogueForm: FC<ICatalogueFormProps> = ({ catalogue, brandId }) => {
  const {
    cataloguesStore: { addCatalogue, editCatalogue },
    alertStore: { showAlert },
    mainStore: { selectedCountryCode },
    popupStore: { openPopup, closePopup, popupName },
  } = useStore();

  const navigate = useNavigate();

  const [scrollTop, setScrollTop] = useState(0);

  const useFormMethods = useForm<ICatalogueForm>({
    defaultValues: {
      ...catalogue,
      brandId,
      countryCode: catalogue ? catalogue.countryCode : selectedCountryCode?.countryCode,
      isNeedValidation: false,
      json: catalogue
        ? { id: catalogue.id, pages: catalogue.pages || [], previewUrl: catalogue.previewUrl }
        : null,
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { isDirty },
  } = useFormMethods;

  const { mutate: addCatalogueMutation, isPending } = useMutation({
    mutationFn: addCatalogue,
    mutationKey: ['addCatalogue'],
    onSuccess: () => {
      showAlert('Catalogue was added succesfully', 'success');
      navigate(`/brands/${brandId}/catalogues`, { replace: true });
    },
  });

  const { mutate: editCatalogueMutation, isPending: editCataloguePending } = useMutation({
    mutationFn: editCatalogue,
    mutationKey: ['editCatalogue'],
    onSuccess: () => {
      showAlert('Catalogue was changed succesfully', 'success');
      navigate(`/brands/${brandId}/catalogues`);
    },
  });

  const { t } = useMyTranslations<
    CatalogueFormTransFields | Extract<CataloguesTransFields, 'addCatalogue'>
  >([catalogueFormTranslations.name, cataloguesTranslations.name]);

  useEffect(() => {
    if (catalogue?.adminMessage) {
      // need to set Timeout beacouse of rendering problems with title prop at MsgActionModal
      setTimeout(() => {
        openPopup('status');
      });
    }
  }, [catalogue?.adminMessage]);

  const sendRequest = useCallback(
    async (formData: ICatalogueForm) => {
      if (catalogue) editCatalogueMutation({ ...catalogue, ...formData });
      else addCatalogueMutation(formData);
      closePopup();
    },
    [catalogue]
  );

  const onSubmit = useCallback(
    (formData: ICatalogueForm) => {
      if (
        catalogue &&
        !compareObjects(catalogue, { ...catalogue, ...formData }, approveRequiredFields)
      ) {
        sendRequest(formData);
      } else {
        setValue('isNeedValidation', true);
        openPopup('confirmation');
      }
    },
    [catalogue]
  );

  const renderSubmitBtn = useCallback(
    (place: string) => (
      <Button
        text={catalogue ? t('save') : t('addCatalogue', { ns: cataloguesTranslations.name })}
        onClick={handleSubmit(onSubmit)}
        disabled={isPending || editCataloguePending || !isDirty}
        styleProp={`max-w-[170px] sm-brand-form-submit ${
          place === 'inside' ? 'md-brand-form-submit-hide' : 'md-brand-form-submit-show'
        }`}
      />
    ),
    [isPending, editCataloguePending, isDirty]
  );

  const maxHeight = useEntityFormMaxHeight();

  const scrollTopDebounced = useDebounce(scrollTop, 100);

  const showToTopButton = useMemo(() => {
    if (scrollTopDebounced > 0) {
      const topFormHeight = document.getElementById('form-top')?.getBoundingClientRect().height;
      return topFormHeight && scrollTopDebounced > topFormHeight;
    }
    return false;
  }, [scrollTopDebounced]);

  const onToTopClick = useCallback(() => {
    const formElement = document.getElementById('form');
    if (formElement) {
      formElement.scrollTo({ top: 0, behavior: 'smooth' });
      setScrollTop(0);
    }
  }, []);

  // TODO change when edit catalogues logic will be implemented
  const disabled = useMemo(() => {
    return false;
    // return catalogue && catalogue.status !== 'Revise';
  }, [catalogue]);

  return (
    // use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error
    // @ts-ignore
    <>
      <FormProvider {...useFormMethods}>
        <form
          id="form"
          className="flex flex-col gap-y-[32px] px-[48px] pb-[48px] sm-page-padding  overflow-scroll h-full"
          style={{ maxHeight }}
          onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        >
          <div className="flex gap-[32px] md-brand-form-right" id="form-top">
            <section className="section">
              <div className="bg-white flex flex-col shadow-sm p-[24px] rounded-[16px] gap-y-[24px] w-[640px] md-brand-form-inputs">
                <h6 className="subtitle !mb-0 !uppercase">{t('basic')}</h6>
                <Input
                  name="name"
                  type="text"
                  required
                  placeholder={t('name')}
                  label={t('name')}
                  showRequiredIcon
                  disabled={disabled}
                />
                <DatePickerRow
                  isTwoRows
                  startName="startDate"
                  startPlaceholder={t('startDate')}
                  endName="expiredDate"
                  endPlaceholder={t('expiredDate')}
                  requiredStart
                  requiredEnd
                  noMaxDateStart
                  noMaxDateEnd
                  minDateEnd={new Date()}
                  label={t('startDate')}
                  endLabel={t('expiredDate')}
                  showRequiredIcons
                  isDisabledStart={disabled}
                  isDisabledEnd={disabled}
                />
              </div>
              {renderSubmitBtn('inside')}
            </section>

            <section className="section md-brand-form-left sm-brand-form-left">
              <ImageUploader name="imageUrl" title={t('pages')} required>
                <UploadJSONInput name="json" disabled={disabled} />
              </ImageUploader>
            </section>
            {renderSubmitBtn('outside')}
          </div>
          {watch('json') && watch('json')!.pages.length > 0 && (
            <div className="bg-white shadow-sm p-[48px] rounded-[16px] w-full grid grid-cols-3 md-page-grid sm-page-grid gap-x-[32px] gap-y-[24px]">
              {watch('json')?.pages.map((page, index) => (
                <div key={index}>
                  <div className="bg-primary text-white rounded-[16px] px-[8px] py-[6px] mb-[16px] w-fit m-auto small-text">
                    {page.position}/{watch('json')?.pages.length}
                  </div>
                  <img
                    src={page.imageUrl}
                    alt={page.position.toString()}
                    className="border-primary border"
                  />
                </div>
              ))}
            </div>
          )}
        </form>
      </FormProvider>
      <Button
        text={'To top'}
        onClick={onToTopClick}
        styleProp={`absolute bottom-[32px] z-100 max-w-[140px] border border-primary gap-x-[8px] transition-[right] duration-300 ${
          showToTopButton ? 'right-[40px]' : 'right-[-200px]'
        }`}
        styleType="secondary"
        icon={<ChevronIcon className="rotate-180" width={16} height={16} />}
        iconPosition="right"
      />
      <Popup width={SECOND_POPUP_WIDTH}>
        {popupName === 'confirmation' && (
          <ConfirmationPopup
            title={t('addCatalogue', { ns: cataloguesTranslations.name })}
            onAccept={() => sendRequest(getValues())}
            text={t('confirmationText')}
          />
        )}
        {popupName === 'status' && catalogue?.adminMessage && (
          <StatusPopup status="Revise" text={catalogue?.adminMessage} />
        )}
      </Popup>
    </>
  );
};

export default observer(CatalogueForm);
